import { defineComponent as _defineComponent } from 'vue'
import OfferRow from '@/features/buybox/components/grid/OfferRow.vue';
import { ObjectType, Offer, Offer as OfferGraphql, PromotedBundleEdge, SraEventAction } from '@/@types/graphql-types';
import { getExcludedPackageShortNames } from '@/constants/package-exclusion';
import { ImpressionTrackingEvents } from '@/enums/events';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { useI18n } from '@/helpers/composables/useI18n';
import { getBuyboxCategoryByMonetizationGraphql } from '@/helpers/offer-helper';
import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { BuyboxOfferCategory } from '@/interfaces/buybox';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { NewBuyBoxView } from '@/stores/useUserStoreTypes';
import { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import { computed, provide } from 'vue';

import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useJWTVCampaign } from '@/features/jwtv-titles-rental/composables/useJWTVCampaign';
import { useJwtvCinematicPromotedOffers } from '@/components/experiments/JWTVTVODCinematicv2/composables/useJwtvCinematicPromotedOffers';
import GridPromotedBuyboxRedesignExp from '@/components/experiments/GridBuyboxRedesignExps/components/GridPromotedBuyboxRedesignExp.vue';
import { useExperiment } from '@/libraries/experiment';
import { useSRFilminCampaign } from '@/helpers/composables/useSRFilminCampaign';
import { getBoneTempleRentOffer } from '@/features/promotions/helpers/boneTemplePromotedCampaign';

type BuyboxRowOffer = TitleOfferFragment & {
	iconUrl?: string;
	icons?: string[];
	bundleId?: number;
	bundleTechnicalName?: string;
};

type OfferCategories =
	| BuyboxOfferCategory.STREAM
	| BuyboxOfferCategory.RENT
	| BuyboxOfferCategory.BUY
	| BuyboxOfferCategory.BUNDLE;


export default /*#__PURE__*/_defineComponent({
  __name: 'PromotedGridBuyBox',
  props: {
    title: null,
    titleId: null,
    titleObjectType: null,
    titleObjectId: null,
    promotedOffers: { default: () => [] },
    promotedBundles: { default: () => [] },
    sponsoredAd: { default: undefined },
    showDetails: { default: null },
    seasonNumber: { default: null },
    episodeNumber: { default: null },
    onlyBundle: { type: Boolean, default: false },
    isMiddleBuyBox: { type: Boolean, default: false },
    buyBoxSwitchType: null,
    additionalContexts: { default: () => [] }
  },
  setup(__props: any) {

const props = __props as { title: TitleDetail, titleId: string, titleObjectType: ObjectType, titleObjectId: number, promotedOffers: TitleOfferFragment[], promotedBundles: PromotedBundleEdge[], sponsoredAd: SponsoredAdFragment | undefined, showDetails: {
			id: string;
			objectId: number;
			objectType: ObjectType;
			title: string;
		} | null, seasonNumber: number | null, episodeNumber: number | null, onlyBundle: boolean, isMiddleBuyBox: boolean, buyBoxSwitchType?: NewBuyBoxView, additionalContexts: SnowplowContext[] };

const CategoryOrder = [
	BuyboxOfferCategory.STREAM,
	BuyboxOfferCategory.RENT,
	BuyboxOfferCategory.BUY,
	BuyboxOfferCategory.BUNDLE,
];



const { t } = useI18n();

const { getPromotedOffersWithJwtv } = useJWTVCampaign();
const { applyJwtvCinematicOverride } = useJwtvCinematicPromotedOffers(() => props.title);
const { isLoggedIn, isProUser } = storeToRefs(useUserStore());
const { country } = storeToRefs(useLanguageStore());

// FILMIN SR CAMPAIGN
const { getCampaignPromotedOffers, isTitleCampaignEligible } = useSRFilminCampaign({
	titleId: props.titleId,
	promotedOffers: props.promotedOffers,
	flatrateOffers: props.title.flatrate ?? [],
});

// BONE TEMPLE SR CAMPAIGN
const boneTempleOffer = computed(() => getBoneTempleRentOffer(country.value, props.title.id));

const allPromotedOffers = computed(() => {
	let offers: TitleOfferFragment[];

	if (!!isTitleCampaignEligible.value) {
		return getCampaignPromotedOffers();
	}

	const normalOffers = getPromotedOffersWithJwtv(props.promotedOffers, props.title);
	offers =
		boneTempleOffer.value != null
			? [...normalOffers, boneTempleOffer.value as unknown as TitleOfferFragment]
			: normalOffers;

	return applyJwtvCinematicOverride(offers);
});

useProvideClickoutPlacement('promoted_buybox');

provide('isMiddleBuyBox', () => props.isMiddleBuyBox);
provide('titleSourceId', props.titleId);

const excludedPackages = computed(() =>
	getExcludedPackageShortNames(
		isLoggedIn.value,
		props.showDetails?.id ?? props.titleId,
		country.value,
		isProUser.value
	)
);

const hasPromotedOffers = computed(
	() =>
		allPromotedOffers.value
			.concat(props.promotedBundles.map(el => el.offer))
			.filter(offer => !excludedPackages.value.includes(offer.package.shortName)).length > 0
);

const { activeVariantWithControlGroup: expGridRedesignClickExpVariant } = useExperiment('EXP_GRID_REDESIGN_CLICK');
const { activeVariantWithControlGroup: expGridRedesignMoreInfoExpVariant } =
	useExperiment('EXP_GRID_REDESIGN_MORE_INFO');

const renderGridRedesignExp = computed(() => {
	if (!hasPromotedOffers.value) return false;
	if (expGridRedesignClickExpVariant.value) return expGridRedesignClickExpVariant.value !== 'control';
	if (expGridRedesignMoreInfoExpVariant.value) return expGridRedesignMoreInfoExpVariant.value !== 'control';
	return false;
});

const categoryTranslatables = computed(() => {
	return {
		[BuyboxOfferCategory.STREAM]: t('WEBAPP_STREAM'),
		[BuyboxOfferCategory.RENT]: t('WEBAPP_RENT'),
		[BuyboxOfferCategory.BUY]: t('WEBAPP_BUY'),
		[BuyboxOfferCategory.BUNDLE]: t('WEBAPP_BUNDLES'),
	};
});

const categories = computed<OfferCategories[]>(() => {
	if (props.onlyBundle) {
		return [BuyboxOfferCategory.BUNDLE];
	}

	const promotedBundleOffers = props.promotedBundles.map(bundle => bundle.offer);

	const updatedOffers = allPromotedOffers.value
		.concat(promotedBundleOffers)
		.map(offer => getBuyboxCategoryByMonetizationGraphql(offer.monetizationType))
		.filter(key => key !== undefined)
		.filter((item, index, offers) => offers.indexOf(item) === index) as OfferCategories[];

	updatedOffers.sort((a, b) => CategoryOrder.indexOf(a) - CategoryOrder.indexOf(b));

	return updatedOffers;
});

function getBundlesByCategory(category: BuyboxOfferCategory): PromotedBundleEdge[] {
	return props.promotedBundles.filter(bundle => {
		if (excludedPackages.value.includes(bundle.offer.package.shortName)) return false;

		return getBuyboxCategoryByMonetizationGraphql(bundle.offer.monetizationType) === category;
	});
}

function getOffersByCategory(category: BuyboxOfferCategory): BuyboxRowOffer[] {
	// Start with the full list of offers
	let categoryOffers = [...allPromotedOffers.value];

	// Filtering out TVOD providers we don't have a partnership with
	const filteredPromotedOffers = categoryOffers.filter(offer => {
		return !excludedPackages.value.includes(offer.package.shortName);
	});

	categoryOffers = filteredPromotedOffers.filter(
		offer => getBuyboxCategoryByMonetizationGraphql(offer.monetizationType) === category
	);

	// Return filtered offers
	return categoryOffers;
}

const { trackSraEvent } = useBackendTracking();

function clickOut(payload: { buyboxOffer: OfferGraphql; isBundle: boolean }) {
	const sponsoredPackage = props.sponsoredAd?.campaign?.watchNowOffer.package;

	if (props.sponsoredAd?.bidId && payload.buyboxOffer.package.packageId === sponsoredPackage?.packageId) {
		const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(payload.buyboxOffer as Offer);
		const contexts = (props.additionalContexts || []).concat([
			new SnowplowTitleContextGraphql(
				props.titleObjectId,
				props.titleObjectType,
				undefined,
				undefined,
				undefined,
				undefined,
				props.title.id
			),
		]);
		contexts.push(clickoutContext);

		TrackingHelper.trackEvent(
			ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
			{
				action: 'buybox_clickout',
				label: `${props.titleId}_${sponsoredPackage.packageId}`,
				property: 'buybox',
				value: props.sponsoredAd?.holdoutGroup ? 0 : 1,
			},
			contexts
		);

		// track influenced campaign metrics
		trackSraEvent({ action: SraEventAction.BuyboxClickout, sponsoredAd: props.sponsoredAd });
	}
}

return { __sfc: true,CategoryOrder, props, t, getPromotedOffersWithJwtv, applyJwtvCinematicOverride, isLoggedIn, isProUser, country, getCampaignPromotedOffers, isTitleCampaignEligible, boneTempleOffer, allPromotedOffers, excludedPackages, hasPromotedOffers, expGridRedesignClickExpVariant, expGridRedesignMoreInfoExpVariant, renderGridRedesignExp, categoryTranslatables, categories, getBundlesByCategory, getOffersByCategory, trackSraEvent, clickOut, OfferRow, GridPromotedBuyboxRedesignExp }
}

})